import React, { useRef, useState } from 'react';
import { Box, Popover, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CustomSelect from '../../ui/primitives/CustomSelect';
import CustomInputField from '../../ui/primitives/CustomInputField';
import ActionButton from '../../ui/primitives/ActionButton';

export const DATE_FILTER_OPTIONS = [
  { id: 'last30', name: 'Last 30 Days' },
  { id: 'last90', name: 'Last 90 Days' },
  { id: 'last6months', name: 'Last 6 Months' },
  { id: 'lastyear', name: 'Last Year' },
  { id: 'custom', name: 'Custom' },
];

export const DEFAULT_DATE_FILTER = { preset: 'last30', startDate: null, endDate: null };


const DashboardDateFilter = ({ value, onChange }) => {
  const anchorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftRange, setDraftRange] = useState({
    startDate: value?.startDate || '',
    endDate: value?.endDate || '',
  });

  const openCustomRangePicker = () => {
    setDraftRange({
      startDate: value?.startDate || '',
      endDate: value?.endDate || '',
    });
    setAnchorEl(anchorRef.current);
  };

  const handlePresetChange = (selectedId) => {
    if (selectedId === 'custom') {
      // Don't apply/refetch yet - just open the picker so the user can choose dates.
      openCustomRangePicker();
    } else {
      setAnchorEl(null);
      onChange({ preset: selectedId, startDate: null, endDate: null });
    }
  };

  const handleCloseCustomRange = () => setAnchorEl(null);

  const handleDraftChange = (field) => (id, val) => {
    setDraftRange((prev) => ({ ...prev, [field]: val }));
  };

  const handleApplyCustomRange = () => {
    onChange({ preset: 'custom', startDate: draftRange.startDate || null, endDate: draftRange.endDate || null });
    handleCloseCustomRange();
  };
  const handleClearCustomRange = () => {
    setDraftRange({ startDate: '', endDate: '' });
    setAnchorEl(null);
    onChange({ preset: 'last30', startDate: null, endDate: null });
  };

  const customRangeLabel =
    value?.preset === 'custom' && value?.startDate && value?.endDate
      ? `${value.startDate} to ${value.endDate}`
      : 'Select dates';

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box ref={anchorRef} sx={{ minWidth: 190 }}>
        <CustomSelect value={value?.preset} onChange={handlePresetChange} options={DATE_FILTER_OPTIONS} />
      </Box>

      {value?.preset === 'custom' && (
        <ActionButton
          label={customRangeLabel}
          icon={<CalendarMonthIcon />}
          variant="outlined"
          color="primary"
          onClick={openCustomRangePicker}
        />
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseCustomRange}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack spacing={2} sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2">Select custom range</Typography>

          <CustomInputField
            id="startDate"
            type="date"
            label="Start date"
            value={draftRange.startDate}
            onChange={handleDraftChange('startDate')}
          />
          <CustomInputField
            id="endDate"
            type="date"
            label="End date"
            value={draftRange.endDate}
            onChange={handleDraftChange('endDate')}
          />

          <Stack direction="row" spacing={1.5}>
            <ActionButton
              label="Clear"
              variant="outlined"
              color="secondary"
              onClick={handleClearCustomRange}
              fullWidth
            />
            <ActionButton label="Apply" intent="primary" onClick={handleApplyCustomRange} fullWidth />
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default DashboardDateFilter;